import axios from 'axios'
import { defaultsDeep } from 'lodash'
import { getEnv } from '../helpers/get-env'
import Config from '@/services/config'
import store from '@/store'

export const retries = 3 // How many times to retry failed requests.
export const region = 'us-east-1'

// Wrapper for API Gateway client
export default class ApiGateway {
  /**
   * Make an unauthenticated request to check if a user has already registered
   * with the given email address.
   *
   * @param {Object} _ref.email - Email address to check.
   * @returns {Promise}
   */
  static async userExists ({ email }) {
    const response = await axios.get(`${Config.ApiGwUrl}/cognito/exists/${encodeURIComponent(email)}`)
    return response.data
  }

  /**
   * Initialize and store an instance of the axios client with interceptors.
   *
   * @param {Object} config - Client config object.
   * @returns {Object} - Config object used when instatiating the client (for testing/debugging).
   */
  static init (config) {
    const defaultConfig = {
      baseURL: config.invokeUrl || Config.ApiGwUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers
      },
      retries,
      retryCondition: this.retryCondition
    }

    const clientConfig = defaultsDeep({}, defaultConfig, config)

    // Create axios instance
    this.client = axios.create({
      baseURL: clientConfig.baseURL,
      timeout: clientConfig.timeout,
      headers: clientConfig.headers
    })

    // Add response interceptor for retry logic
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config

        // Initialize retry counter if it doesn't exist
        if (!config.__retryCounter) {
          config.__retryCounter = 0
        }

        // Check if we should retry
        if (config.__retryCounter < clientConfig.retries && clientConfig.retryCondition(error)) {
          config.__retryCounter++

          // Add exponential backoff delay
          const delay = Math.pow(2, config.__retryCounter) * 1000
          await new Promise(resolve => setTimeout(resolve, delay))

          return this.client.request(config)
        }

        return Promise.reject(error)
      }
    )

    return clientConfig
  }

  /**
   * Callback to tell client wether or not a failed request should be retried.
   * @param {Error} error - Axios error object from failed request.
   * @returns {Boolean}
   */
  static retryCondition (error) {
    if (!error.response) return false
    return error.response.status >= 500
  }

  /**
   * Substitute path template variables with actual values
   * @param {String} pathTemplate - Path template with {param} placeholders
   * @param {Object} params - Object containing parameter values
   * @returns {String} - Path with substituted values
   */
  static _substitutePath (pathTemplate, params = {}) {
    return pathTemplate.replace(/\{([^}]+)\}/g, (match, key) => {
      if (params[key] !== undefined) {
        return encodeURIComponent(params[key])
      }
      return match
    })
  }

  /**
   * Build query string from parameters
   * @param {Object} queryParams - Query parameters object
   * @returns {String} - URL encoded query string
   */
  static _buildQueryString (queryParams = {}) {
    const params = new URLSearchParams()
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] !== undefined && queryParams[key] !== null) {
        params.append(key, queryParams[key])
      }
    })
    const queryString = params.toString()
    return queryString ? `?${queryString}` : ''
  }

  /**
   * Proxy method for invoking the API.
   *
   * @param {Object} arguments - A hash of arguments for the proxied method.
   * @returns {Promise}
   */
  static async invokeApi ({
    params,
    pathTemplate,
    method,
    additionalParams,
    body
  }) {
    additionalParams = this._addDevCognitoHeaders(additionalParams)

    // Build the full URL
    const path = this._substitutePath(pathTemplate, params)
    const queryString = this._buildQueryString(additionalParams?.queryParams)
    const url = `${path}${queryString}`

    // Prepare request config
    const requestConfig = {
      method: method.toLowerCase(),
      url,
      ...additionalParams
    }

    // Add body for methods that support it
    if (body && [
      'post',
      'put',
      'patch'
    ].includes(requestConfig.method)) {
      requestConfig.data = body
    }

    // Remove queryParams from config since we've already processed them
    if (requestConfig.queryParams) {
      delete requestConfig.queryParams
    }

    try {
      const response = await this.client.request(requestConfig)
      return response
    } catch (e) {
      if (e.response && e.response.status === 401) {
        await store.dispatch('getCurrentUser') // Try to refresh the session token.
        const response = await this.client.request(requestConfig)
        return response
      }
      throw e
    }
  }

  /**
   * This is a hack to give a locally-running Serverless service access to the
   * developer's Cognito Identity ID via a custom header.
   *
   * Normally when deployed to AWS, the lambda can access this ID via the
   * `event` object. But when running locally with `serverless-offline` the
   * event only contains a generic placeholder value instead of the actual ID.
   *
   * @param {Object} additionalParams
   * @returns {Object}
   */
  static _addDevCognitoHeaders (additionalParams) {
    if (getEnv() !== 'local') return additionalParams
    const headers = { 'Local-Developer-Cognito-Identity-Id': Config.getCurrentUserCognitoUsername() }
    additionalParams = defaultsDeep({ headers }, additionalParams)
    return additionalParams
  }
}
